import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { Bars } from "react-loader-spinner";
import { ethers } from "ethers";

import Container from "../components/common/Container";
import CircleStep from "../components/common/CircleStep";
import MetaTags from "../components/common/MetaTags";
import notify from "../components/common/notify";

import Step1BasicInfo from "../components/CreateCommunity/Step1BasicInfo";
import Step2CommunityLinks from "../components/CreateCommunity/Step2CommunityLinks";
import Step3SaleSettings from "../components/CreateCommunity/Step3SaleSettings";
import Step4PerksAccess from "../components/CreateCommunity/Step4PerksAccess";
import Step5AdvancedSettings from "../components/CreateCommunity/Step5AdvancedSettings";
import Step6Preview from "../components/CreateCommunity/Step6Preview";

import {
  useSaveCommunityMutation,
  useEditCommunityMutation,
  useLazyGetCommunityByIdQuery,
  usePublishCommunityMutation,
  useUploadCommunityMetadataMutation,
  useUpdateDeploymentStatusMutation,
} from "../redux/slices/api/communityApiSlice";
import { useLazyGetUserQuery } from "../redux/slices/api/userApi";
import { useSelector, useDispatch } from "react-redux";
import { selectGeneral, setShowAuthModal, setShowWalletRequiredModal } from "../redux/slices/generalSlice";
import { getChain, contractNetworks, currencies } from "../utils/currencies";
import CommunityFactoryV2ABI from "../data/abi/CommunityMembershipFactoryV2.json";
import useTransactionMonitor from "../hooks/useTransactionMonitor";
import useApi from "../hooks/useApi";
import useWalletAuth from "../hooks/useWalletAuth";
import useCheckNetwork from "../hooks/useCheckNetwork";
import { publicClientToProvider } from "../hooks/useProvider";
import { formatWalletAddress } from "../utils/walletDisplay";

// Validation schema
const validationSchema = yup.object({
  name: yup
    .string()
    .required("Community name is required")
    .max(100, "Name must be 100 characters or less"),
  shortDescription: yup
    .string()
    .required("Short description is required")
    .max(300, "Description must be 300 characters or less"),
  artwork: yup.string().required("NFT artwork is required"),
  category: yup.string().required("Please select a category"),
  totalSupply: yup
    .number()
    .required("Total supply is required")
    .min(1, "Minimum supply is 1")
    .max(1000, "Maximum supply is 1000"),
  blockchain: yup.string().required("Please select a blockchain"),
  saleSettings: yup.object({
    price: yup
      .number()
      .required("Membership price is required")
      .min(0, "Price cannot be negative"),
    currency: yup.string().required("Please select a currency"),
  }),
});

// Steps configuration
const steps = [
  { label: "Basic Info", description: "Name, description & artwork" },
  { label: "Links", description: "Community channels" },
  { label: "Sale", description: "Pricing & supply" },
  { label: "Perks", description: "Member benefits" },
  { label: "Advanced", description: "Blockchain & royalties" },
  { label: "Preview", description: "Review & publish" },
];

const ChangeStepButton = ({ type = "next", label, onClick, disabled }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 font-medium text-sm sm:text-base
        ${
          type === "next" 
            ? "bg-gradient-to-r from-primary to-purple-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105" 
            : "text-gray-600 hover:text-primary px-4 py-2 rounded-lg hover:bg-gray-50"
        } 
        transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 min-w-[140px] sm:min-w-[160px]`}
    >
      {type === "prev" && (
        <BsChevronLeft className="text-lg" />
      )}
      <span className="font-semibold">
        {label ?? (type === "next" ? "Continue" : "Back")}
      </span>
      {type === "next" && (
        <BsChevronRight className="text-lg" />
      )}
    </button>
  );
};

const CreateCommunity = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { address, isConnected } = useAccount();
  const { isLoggedIn } = useSelector(selectGeneral);
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  // Read-only provider built from wagmi's transport (Alchemy → Ankr → public fallback).
  // Used for non-signing calls (getBalance, getFeeData, estimateGas) so they bypass
  // MetaMask's own RPC endpoint, which can be rate-limited on Polygon.
  const readProvider = useMemo(() => publicClientToProvider(publicClient), [publicClient]);
  
  // Use wallet auth hook for proper wallet checks
  const { ensureWalletReady, hasLinkedWallet } = useWalletAuth();
  
  // Network switching hook
  const { switchNetwork, switchNetworkLoading } = useCheckNetwork();

  // URL params for edit mode
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishingStep, setPublishingStep] = useState("");
  const [communityId, setCommunityId] = useState(editId || null);

  // Local storage key for draft backup
  const DRAFT_STORAGE_KEY = "community_draft_backup";

  // Load draft from localStorage if no editId
  const loadDraftFromStorage = useCallback(() => {
    if (!editId) {
      try {
        const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (savedDraft) {
          const parsed = JSON.parse(savedDraft);
          return parsed;
        }
      } catch (e) {
        console.log("Failed to load draft from storage:", e);
      }
    }
    return null;
  }, [editId]);

  // Save draft to localStorage
  const saveDraftToStorage = useCallback((values) => {
    if (!editId) {
      try {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(values));
      } catch (e) {
        console.log("Failed to save draft to storage:", e);
      }
    }
  }, [editId]);

  // Clear draft from localStorage
  const clearDraftFromStorage = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  }, []);

  // API hooks
  const [saveCommunity, { isLoading: saving }] = useSaveCommunityMutation();
  const [editCommunity, { isLoading: editing }] = useEditCommunityMutation();
  const [publishCommunity] = usePublishCommunityMutation();
  const [uploadMetadata] = useUploadCommunityMetadataMutation();
  const [updateDeploymentStatus] = useUpdateDeploymentStatusMutation();
  const [getCommunityById, { data: existingCommunity, isLoading: loadingCommunity }] =
    useLazyGetCommunityByIdQuery();
  const [triggerGetUser, { data: userData }] = useLazyGetUserQuery();
  
  // File upload hook
  const { loading: uploadingImageApi, PUBLIC_REQUEST } = useApi();

  // Transaction monitoring
  const {
    pendingTx,
    txStatus,
    elapsedTime,
    isMonitoring,
    monitorTransaction,
    retryTransaction,
    cancelMonitoring,
  } = useTransactionMonitor({
    communityId,
    onSuccess: async (result) => {
      try {
        // Transaction confirmed! Parse contract address from events
        setPublishingStep("Finalizing community...");
        
        let contractAddress = null;
        
        // Try to parse CommunityCreated event from the V2 factory transaction
        // The event signature: CommunityCreated(address indexed creator, address indexed communityContract, ...)
        // communityContract is the 2nd indexed parameter, so it's in topics[2]
        if (result.receipt?.logs) {
          try {
            // CommunityCreated event signature hash
            const communityCreatedTopic = ethers.id("CommunityCreated(address,address,string,string,uint256,uint256,bool,address)");
            
            console.log("Looking for CommunityCreated event with topic:", communityCreatedTopic);
            console.log("Total logs in receipt:", result.receipt.logs.length);
            
            for (const log of result.receipt.logs) {
              // Handle both viem and ethers log formats
              const topics = log.topics || [];
              
              console.log("Checking log:", {
                address: log.address,
                topics: topics,
                topicsLength: topics.length
              });
              
              // Check if this is the CommunityCreated event
              if (topics[0]?.toLowerCase() === communityCreatedTopic.toLowerCase()) {
                console.log("Found CommunityCreated event! Topics:", topics);
                // communityContract is indexed at position 2 (topics[2])
                // Note: topics[0] = event signature, topics[1] = creator, topics[2] = communityContract
                if (topics[2]) {
                  // Remove padding: 0x000000000000000000000000<address>
                  const rawTopic = topics[2];
                  contractAddress = ethers.getAddress("0x" + rawTopic.slice(-40));
                  console.log("Parsed community contract from event topic:", contractAddress);
                  break;
                } else if (topics[1]) {
                  // Fallback: maybe the indexed parameters are in different order
                  const rawTopic = topics[1];
                  contractAddress = ethers.getAddress("0x" + rawTopic.slice(-40));
                  console.log("Parsed community contract from topics[1]:", contractAddress);
                  break;
                }
              }
            }
            
            // Fallback: Try using ethers Interface if factoryContract is available
            if (!contractAddress && result.metadata?.factoryContract) {
              console.log("Trying to parse using factory contract interface...");
              for (const log of result.receipt.logs) {
                try {
                  const parsed = result.metadata.factoryContract.interface.parseLog({
                    topics: Array.isArray(log.topics) ? log.topics : [],
                    data: log.data || "0x"
                  });
                  console.log("Parsed log:", parsed?.name, parsed?.args);
                  if (parsed?.name === "CommunityCreated") {
                    contractAddress = parsed.args.communityContract;
                    console.log("Parsed community contract from interface:", contractAddress);
                    break;
                  }
                } catch (e) {
                  // Not a matching event, continue
                }
              }
            }
            
            // Additional fallback: Try to find any Transfer event from a newly deployed contract
            // This happens when preMintMode is true
            if (!contractAddress) {
              console.log("Trying to find contract from Transfer events...");
              const transferTopic = ethers.id("Transfer(address,address,uint256)");
              const factoryAddress = result.metadata?.factoryContract?.target || result.metadata?.factoryContract?.address;
              
              for (const log of result.receipt.logs) {
                const topics = log.topics || [];
                if (topics[0]?.toLowerCase() === transferTopic.toLowerCase()) {
                  // Check if this Transfer is from address(0) - indicating a mint
                  // topics[1] is the 'from' address
                  if (topics[1] && topics[1].toLowerCase().endsWith("0".repeat(40))) {
                    // This is a mint event, log.address should be the NFT contract
                    try {
                      contractAddress = ethers.getAddress(log.address);
                      console.log("Found potential contract from Transfer event:", contractAddress);
                      // Verify it's not the factory contract itself (if we have factory address)
                      if (factoryAddress && contractAddress.toLowerCase() === factoryAddress.toLowerCase()) {
                        console.log("Skipping factory contract address");
                        contractAddress = null;
                        continue;
                      }
                      break;
                    } catch (e) {
                      console.error("Invalid address from Transfer event:", e);
                    }
                  }
                }
              }
            }
          } catch (parseErr) {
            console.error("Error parsing event:", parseErr);
          }
        }
        
        // Validate contract address
        if (contractAddress) {
          try {
            // Ensure it's a valid checksum address
            contractAddress = ethers.getAddress(contractAddress);
            console.log("Final validated contract address:", contractAddress);
          } catch (e) {
            console.error("Invalid contract address format:", contractAddress, e);
            contractAddress = null;
          }
        }
        
        // Fallback: use a placeholder if parsing failed
        if (!contractAddress) {
          console.error("Could not parse contract address from events.");
          console.error("Total logs:", result.receipt?.logs?.length);
          console.error("Receipt:", JSON.stringify(result.receipt, null, 2));
          
          // Last resort: ask user to manually verify and provide contract address
          const userProvidedAddress = prompt(
            "Could not automatically detect the deployed contract address from the blockchain.\n\n" +
            "Please check your wallet transaction history or block explorer (e.g., Sepolia Etherscan) " +
            "and enter the newly deployed Community NFT contract address:\n\n" +
            "Transaction Hash: " + (result.receipt?.transactionHash || result.receipt?.hash || "N/A")
          );
          
          if (userProvidedAddress) {
            try {
              contractAddress = ethers.getAddress(userProvidedAddress.trim());
              console.log("User provided contract address:", contractAddress);
            } catch (e) {
              console.error("Invalid address provided by user:", e);
              notify("Invalid contract address format. Please try again.", "error");
              setPublishing(false);
              setPublishingStep("");
              return;
            }
          } else {
            notify("Contract address is required to complete community creation.", "error");
            setPublishing(false);
            setPublishingStep("");
            return;
          }
        }

        // Step 8: If preMintMode is enabled, batch mint NFTs to creator
        const preMintMode = formik.values.preMintMode || false;
        const totalSupply = formik.values.totalSupply || 10;
        
        if (preMintMode) {
          let batchMintSuccess = false;
          try {
            setPublishingStep(`Pre-minting ${totalSupply} NFTs to your wallet... (⚠️ Please approve the wallet transaction)`);

            const CommunityMembershipV2ABI = (await import("../data/abi/CommunityMembershipV2.json")).default;

            // Ensure MetaMask's Polygon RPC is updated before asking user to sign
            await fixMetaMaskRpc(walletClient?.chain?.id);

            // Pre-fetch gas, nonce, and fees via publicClient (Alchemy/drpc.org) so that
            // MetaMask only receives eth_sendTransaction — bypasses MetaMask's broken
            // gas estimation on Polygon (which returns ~21676 = base cost only).
            let batchMintGas, batchMintNonce, batchMintMaxFee, batchMintPriorityFee;
            try {
              [batchMintGas, batchMintNonce] = await Promise.all([
                publicClient.estimateContractGas({
                  address: contractAddress,
                  abi: CommunityMembershipV2ABI,
                  functionName: "batchMintToCreator",
                  args: [BigInt(totalSupply)],
                  account: address,
                }),
                publicClient.getTransactionCount({ address }),
              ]);
              batchMintGas = batchMintGas * 12n / 10n; // 20% buffer
              const fees = await publicClient.estimateFeesPerGas();
              batchMintMaxFee = fees.maxFeePerGas;
              batchMintPriorityFee = fees.maxPriorityFeePerGas;
            } catch (gasErr) {
              console.warn("Gas pre-fetch failed, letting walletClient handle it:", gasErr);
            }
            
            // Batch mint NFTs to creator
            console.log(`Batch minting ${totalSupply} NFTs to creator...`);
            const batchMintHash = await walletClient.writeContract({
              address: contractAddress,
              abi: CommunityMembershipV2ABI,
              functionName: "batchMintToCreator",
              args: [BigInt(totalSupply)],
              account: address,
              chain: walletClient?.chain,
              ...(batchMintGas !== undefined && { gas: batchMintGas }),
              ...(batchMintNonce !== undefined && { nonce: batchMintNonce }),
              ...(batchMintMaxFee !== undefined && { maxFeePerGas: batchMintMaxFee }),
              ...(batchMintPriorityFee !== undefined && { maxPriorityFeePerGas: batchMintPriorityFee }),
            });
            
            setPublishingStep("Confirming pre-mint transaction...");
            const batchMintReceipt = await readProvider.waitForTransaction(batchMintHash);
            
            console.log("Batch mint successful! Receipt:", batchMintReceipt);
            notify(`Successfully pre-minted ${totalSupply} NFTs to your wallet!`, "success");
            batchMintSuccess = true;
          } catch (batchMintErr) {
            console.error("Batch mint error:", batchMintErr);
            
            // Save the contract address so the creator can retry without re-deploying
            try {
              await updateDeploymentStatus({
                id: communityId,
                body: {
                  contractAddress,
                  status: "failed",
                  errorMessage: batchMintErr?.message || "Batch mint failed",
                },
              }).unwrap();
            } catch (statusErr) {
              console.error("Failed to save contract address after batch mint failure:", statusErr);
            }

            const isRejected = batchMintErr?.code === "ACTION_REJECTED" || batchMintErr?.code === 4001;
            const errorMsg = isRejected
              ? `You rejected the NFT minting transaction. Your community contract has been deployed but NFTs have not been minted yet — members cannot join until you complete this step.\n\nTo finish setup, open this community in edit mode and click "Publish" again.`
              : `Pre-minting failed: ${batchMintErr?.message || "Unknown error"}. Your community contract was deployed but NFTs were not minted.\n\nTo finish setup, open this community in edit mode and click "Publish" again.`;

            notify(errorMsg, "error");
            setPublishing(false);
            setPublishingStep("");
            // Navigate to the community page (state is "pending_mint") so creator can see the issue
            navigate(`/communities/${communityId}`);
            return; // IMPORTANT: do NOT call publishCommunity without pre-minted NFTs
          }

          if (!batchMintSuccess) return;
        }

        await publishCommunity({
          id: communityId,
          body: {
            txHash: result.receipt.transactionHash || result.receipt.hash,
            contractAddress: contractAddress,
            metadataCid: result.metadata?.metadataCid,
            network: formik.values.blockchain,
            blockNumber: Number(result.blockNumber),
            gasUsed: result.gasUsed,
          },
        }).unwrap();

        clearDraftFromStorage();
        
        // Notify about fee recipient if different from creator
        const receiverWallet = formik.values.royaltyRecipient;
        if (receiverWallet && receiverWallet.toLowerCase() !== address?.toLowerCase()) {
          notify(`Community published successfully! 🎉 Membership fees will be sent to: ${formatWalletAddress(receiverWallet)}`, "success");
        } else {
          notify("Community published successfully! 🎉", "success");
        }
        
        navigate(`/communities/${communityId}`);
      } catch (err) {
        console.error("Backend finalization error:", err);
        notify("Transaction confirmed but backend update failed. Please contact support.", "error");
      } finally {
        setPublishing(false);
        setPublishingStep("");
      }
    },
    onError: (error) => {
      console.error("Transaction failed:", error);
      setPublishing(false);
      setPublishingStep("");
      notify("Transaction failed. You can retry from your community dashboard.", "error");
    },
  });

  // Check user permissions
  useEffect(() => {
    if (isLoggedIn) {
      triggerGetUser();
    }
  }, [isLoggedIn, triggerGetUser]);

  // Load existing community for editing
  useEffect(() => {
    if (editId) {
      getCommunityById({ id: editId });
    }
  }, [editId, getCommunityById]);

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(setShowAuthModal({ show: true, redirect: '/create-community' }));
    }
  }, [isLoggedIn, dispatch]);

  // Redirect if profile not completed - allow both individuals and organizations
  useEffect(() => {
    if (userData && !userData.user?.profile_completed) {
      notify("Please complete your profile first", "info");
      navigate("/create-profile");
    }
  }, [userData, navigate]);

  // Initial form values - check localStorage backup first
  const initialValues = useMemo(() => {
    // Priority: existingCommunity (from server) > localStorage backup > defaults
    if (existingCommunity) {
      // Clear localStorage backup since we have server data
      clearDraftFromStorage();
      return {
        name: existingCommunity.name || "",
        shortDescription: existingCommunity.shortDescription || "",
        longDescription: existingCommunity.longDescription || "",
        artwork: existingCommunity.artwork || "",
        bannerImage: existingCommunity.bannerImage || "",
        category: existingCommunity.category || "",
        membershipTier: existingCommunity.membershipTier || "custom",
        membershipDuration: existingCommunity.membershipDuration || "lifetime",
        totalSupply: existingCommunity.totalSupply || 10,
        saleSettings: {
          price: existingCommunity.saleSettings?.price || 0,
          currency: existingCommunity.saleSettings?.currency || "ETH",
        },
        royalties: existingCommunity.royalties || 5,
        royaltyRecipient: existingCommunity.royaltyRecipient || address || "",
        links: existingCommunity.links || {},
        perks: existingCommunity.perks || [],
        perkChecklist: existingCommunity.perkChecklist || {},
        blockchain: existingCommunity.blockchain || "sepolia",
        attributes: existingCommunity.attributes || [],
        preMintMode: existingCommunity.preMintMode || false,
      };
    }

    // Check localStorage for backup draft
    const savedDraft = loadDraftFromStorage();
    if (savedDraft && savedDraft.name) {
      // Show notification that draft was restored
      notify("Restored your previous draft", "info");
      return savedDraft;
    }

    // Default values
    return {
      name: "",
      shortDescription: "",
      longDescription: "",
      artwork: "",
      bannerImage: "",
      category: "",
      membershipTier: "custom",
      membershipDuration: "lifetime",
      totalSupply: 10,
      saleSettings: {
        price: 0,
        currency: "ETH",
      },
      royalties: 5,
      royaltyRecipient: address || "",
      links: {},
      perks: [],
      perkChecklist: {},
      blockchain: "sepolia",
      attributes: [],
      preMintMode: false, // Default: members mint their own NFTs
    };
  }, [existingCommunity, address, loadDraftFromStorage, clearDraftFromStorage]);

  // Formik setup
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (communityId) {
          await editCommunity({ id: communityId, body: values }).unwrap();
          notify("Community saved successfully", "success");
        } else {
          const result = await saveCommunity({ body: values }).unwrap();
          setCommunityId(result.community._id);
          notify("Community draft created", "success");
        }
      } catch (err) {
        notify(err?.data?.message || "Failed to save community", "error");
      }
    },
  });

  // Handle image upload
  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      notify("Image size must be less than 10MB", "error");
      return;
    }

    setUploadingImage(true);

    try {
      // Upload image to server using useApi hook
      const formData = new FormData();
      formData.append('file', file);
      
      const onSuccess = (data) => {
        formik.setFieldValue(fieldName, data.link);
        notify(data.message || "Image uploaded successfully", "success");
        setUploadingImage(false);
      };
      
      const onError = (error) => {
        console.error("Image upload error:", error);
        if (error.status === 413) {
          notify("The size of the uploaded image is large, please try again.", "error");
        } else {
          notify("Failed to upload image", "error");
        }
        setUploadingImage(false);
      };
      
      await PUBLIC_REQUEST({
        url: "/upload",
        method: "post",
        data: formData,
        notifyError: true,
        onSuccess,
        onError,
      });
      
    } catch (err) {
      console.error("Image upload error:", err);
      notify("Failed to upload image", "error");
      setUploadingImage(false);
    }
  };

  // Handle publish
  /**
   * Attempt to update MetaMask's configured RPC for a network to a working endpoint.
   * MetaMask's default Polygon RPC can be broken/unreachable, causing eth_sendTransaction
   * to fail with -32603 even after the user approves the transaction.
   * wallet_addEthereumChain shows an "Update network?" dialog when the chain already exists
   * with different params — once the user approves, MetaMask uses our Alchemy/drpc.org URL.
   * Uses sessionStorage so the dialog only appears once per browser session.
   */
  const fixMetaMaskRpc = async (chainId) => {
    if (chainId !== 137 || !window.ethereum) return;
    const SESSION_KEY = "fds_polygon_rpc_fixed";
    if (sessionStorage.getItem(SESSION_KEY)) return;
    try {
      const alchemyKey = process.env.REACT_APP_ALCHEMY_API_KEY;
      const rpcUrls = [
        ...(alchemyKey ? [`https://polygon-mainnet.g.alchemy.com/v2/${alchemyKey}`] : []),
        "https://polygon.drpc.org",
      ];
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: "0x89",
          chainName: "Polygon Mainnet",
          nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
          rpcUrls,
          blockExplorerUrls: ["https://polygonscan.com"],
        }],
      });
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (e) {
      // Non-fatal: user may have rejected the update dialog — log and continue
      console.warn("MetaMask Polygon RPC update skipped:", e?.message || e);
    }
  };

  const handlePublish = async () => {
    // Check wallet connection using ensureWalletReady for proper flow
    const { ready, reason } = await ensureWalletReady("deploy your community NFT collection", `/create-community${communityId ? `?edit=${communityId}` : ''}`);
    if (!ready) {
      if (reason === "not_connected") {
        notify("Please connect your wallet to deploy the NFT collection", "info");
      }
      return;
    }

    // Validate form first
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      notify("Please fix the errors before publishing", "error");
      return;
    }

    // Check at least one link
    const hasLink = Object.values(formik.values.links || {}).some(
      (link) => link && link.trim() !== ""
    );
    if (!hasLink) {
      notify("Please add at least one community link", "error");
      setActiveStep(1);
      return;
    }

    setPublishing(true);

    let deploymentStarted = false;
    try {
      // Step 1: Save the community data first
      setPublishingStep("Saving community data...");
      let currentId = communityId;
      
      if (!currentId) {
        const saveResult = await saveCommunity({ body: formik.values }).unwrap();
        currentId = saveResult.community._id;
        setCommunityId(currentId);
        notify("Community saved successfully", "success");
      } else {
        await editCommunity({ id: currentId, body: formik.values }).unwrap();
        notify("Community updated successfully", "success");
      }

      // Step 2: Upload metadata to IPFS
      setPublishingStep("Uploading metadata to IPFS...");
      const metadataResult = await uploadMetadata({ id: currentId }).unwrap();
      const metadataCid = metadataResult.cid;

      // Step 3: Get blockchain network details
      // Handle both chainId (number) and network name (string)
      const blockchainValue = formik.values.blockchain;
      const chainConfig = typeof blockchainValue === 'number' 
        ? getChain({ chainId: blockchainValue }) 
        : getChain({ network: blockchainValue }) || getChain({ chainId: Number(blockchainValue) });
      const network = chainConfig?.network || blockchainValue;
      // wagmi's publicClient.chain has the correct nativeCurrency (with our POL override)
      const nativeCurrencySymbol = publicClient?.chain?.nativeCurrency?.symbol || chainConfig?.shortName || "ETH";

      // Step 3b: Check if this is a retry for batch minting (contract already deployed but batch mint failed)
      const existingContractAddress = existingCommunity?.nftDetails?.contractAddress;
      const isRetryBatchMint =
        formik.values.preMintMode &&
        existingContractAddress &&
        existingContractAddress !== "0x" + "0".repeat(40) &&
        (existingCommunity?.state === "pending_mint" || existingCommunity?.state === "deploying");

      if (isRetryBatchMint) {
        setPublishingStep("Switching to the correct network...");
        try {
          const { isSwitched } = await switchNetwork(network);
          if (isSwitched) await new Promise((resolve) => setTimeout(resolve, 1500));
        } catch (switchError) {
          throw new Error(`Please switch to ${chainConfig?.name || network} network in your wallet.`);
        }

        setPublishingStep(`Re-minting ${formik.values.totalSupply || 10} NFTs to your wallet... (⚠️ Please approve the wallet transaction)`);
        const CommunityMembershipV2ABI = (await import("../data/abi/CommunityMembershipV2.json")).default;

        // Ensure MetaMask's broadcast RPC is working before asking user to sign
        await fixMetaMaskRpc(walletClient.chain?.id);

        // Use walletClient.writeContract so viem routes reads (fee/nonce) through
        // wagmi's configured transports (Alchemy/Ankr) instead of MetaMask's own RPC.
        const batchMintHash = await walletClient.writeContract({
          address: existingContractAddress,
          abi: CommunityMembershipV2ABI,
          functionName: "batchMintToCreator",
          args: [BigInt(formik.values.totalSupply || 10)],
          account: address,
          chain: walletClient.chain,
        });
        setPublishingStep("Confirming pre-mint transaction...");
        await readProvider.waitForTransaction(batchMintHash);
        notify(`Successfully pre-minted ${formik.values.totalSupply || 10} NFTs!`, "success");

        // Now publish the community
        await publishCommunity({
          id: currentId,
          body: {
            txHash: batchMintHash,
            contractAddress: existingContractAddress,
            metadataCid,
            network,
            blockNumber: 0,
            gasUsed: 0,
          },
        }).unwrap();

        clearDraftFromStorage();
        notify("Community published successfully! 🎉", "success");
        navigate(`/communities/${currentId}`);
        return;
      }

      // Step 4: Check if we have contract configuration for this network
      const networkContracts = contractNetworks?.[network];
      if (!networkContracts?.communityFactory) {
        // If no community factory contract configured, proceed with mock deployment
        setPublishingStep("Completing setup...");
        await publishCommunity({
          id: currentId,
          body: {
            txHash: "0x" + "0".repeat(64),
            contractAddress: "0x" + "0".repeat(40),
            tokenId: 0, // Single token for mock
            metadataCid: metadataCid,
          },
        }).unwrap();
        
        // Clear localStorage backup on successful publish
        clearDraftFromStorage();
        
        notify("Community published successfully! 🎉", "success");
        navigate(`/communities/${currentId}`);
        return;
      }

      // Step 5: Switch to the correct network
      setPublishingStep("Switching to the correct network...");
      try {
        const { isSwitched } = await switchNetwork(network);
        if (isSwitched) {
          // Give the wallet time to settle after network switch
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      } catch (switchError) {
        console.error("Network switch error:", switchError);
        throw new Error(`Please switch to ${chainConfig?.name || network} network in your wallet to deploy the community.`);
      }

      // Step 6: Update state to 'deploying'
      setPublishingStep("Preparing blockchain deployment...");
      await updateDeploymentStatus({
        id: currentId,
        body: {
          status: "pending",
        },
      }).unwrap();
      deploymentStarted = true;

      // Step 7: Deploy NFT Collection contract
      setPublishingStep("Deploying NFT collection on-chain...");
      
      if (!walletClient) {
        throw new Error("Wallet client not available");
      }

      // Validate that we have a proper community name before deploying
      const communityName = formik.values.name?.trim();
      if (!communityName || communityName.length < 2) {
        throw new Error("Community name is required. Please enter a valid community name.");
      }

      // Read-only contract instance used only for estimateGas (uses readProvider, not MetaMask RPC)
      const factoryContract = new ethers.Contract(
        networkContracts.communityFactory,
        CommunityFactoryV2ABI,
        readProvider
      );

      // Create Community NFT collection with V2 parameters
      // 95% goes to creator, 5% goes to platform automatically
      const royaltyFee = (formik.values.royalties || 5) * 100; // Convert to basis points (max 1000 = 10%)
      
      // Get currency info to use correct decimals and payment token
      const selectedCurrency = formik.values.saleSettings?.currency || "ETH";
      const currencyInfo = currencies[selectedCurrency];
      const decimals = currencyInfo?.decimals || 18;
      const isNativeToken = currencyInfo?.isNativeToken ?? true;
      
      // Convert price using correct decimals for the selected token
      const priceInSmallestUnit = ethers.parseUnits(
        String(formik.values.saleSettings?.price || 0),
        decimals
      );
      
      // Set payment token address (ZeroAddress for native tokens like ETH/POL/BNB)
      let paymentToken = ethers.ZeroAddress;
      if (!isNativeToken) {
        const blockchain = formik.values.blockchain || "sepolia";
        const tokenContract = currencyInfo?.chain?.[blockchain]?.contract;
        if (!tokenContract || tokenContract === "0x" + "0".repeat(40)) {
          throw new Error(
            `${selectedCurrency} is not supported on ${blockchain}. Please select a different currency or blockchain.`
          );
        }
        paymentToken = tokenContract;
      }
      
      const preMintMode = formik.values.preMintMode || false;
      
      // Generate a meaningful symbol from the name (first 5 chars, uppercase, alphanumeric only)
      const symbol = communityName
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 5)
        .toUpperCase() || "COMM";
      
      console.log("Deploying community NFT collection:", {
        name: communityName,
        symbol,
        maxSupply: formik.values.totalSupply,
        price: priceInSmallestUnit.toString(),
        currency: selectedCurrency,
        decimals,
        paymentToken,
        isNativeToken,
        preMintMode,
      });

      // Pre-flight check: estimate gas and verify user has enough balance
      setPublishingStep("Estimating deployment cost...");
      const createCommunityArgs = [
        communityName, // name
        symbol, // symbol
        formik.values.totalSupply || 1000, // maxSupply
        priceInSmallestUnit, // price
        paymentToken, // payment token
        Math.min(royaltyFee, 1000), // royalty fee
        `ipfs://${metadataCid}`, // baseURI
        preMintMode, // preMintMode
        formik.values.royaltyRecipient && formik.values.royaltyRecipient.toLowerCase() !== address?.toLowerCase() 
          ? formik.values.royaltyRecipient 
          : ethers.ZeroAddress // feeRecipient
      ];

      // Pre-fetch tx overrides via publicClient (Alchemy → Ankr → drpc.org) so that
      // walletClient.writeContract only sends eth_sendTransaction to MetaMask.
      // Without these, viem's prepareTransactionRequest fetches nonce/fees/gas through
      // MetaMask's own configured RPC, which returns HTTP 405/-32080 on Polygon.
      let txGas = undefined;
      let txNonce = undefined;
      let txMaxFeePerGas = undefined;
      let txMaxPriorityFeePerGas = undefined;

      try {
        // factoryContract uses readProvider (wagmi transport: Alchemy → Ankr → drpc.org)
        // so this never touches MetaMask's own RPC.
        const estimatedGas = await factoryContract.createCommunity.estimateGas(
          ...createCommunityArgs,
          { from: address }
        );
        txGas = estimatedGas * 120n / 100n; // 20% gas buffer

        const feeData = await readProvider.getFeeData();
        const gasPrice = feeData.gasPrice || feeData.maxFeePerGas || ethers.parseUnits("50", "gwei");
        const estimatedCost = estimatedGas * gasPrice;
        const balanceBuffer = estimatedCost * BigInt(120) / BigInt(100);

        // Capture fee params to pass to writeContract (avoids MetaMask fee RPC calls)
        if (feeData.maxFeePerGas) {
          txMaxFeePerGas = feeData.maxFeePerGas;
          txMaxPriorityFeePerGas = feeData.maxPriorityFeePerGas || ethers.parseUnits("30", "gwei");
        }
        
        const userBalance = await readProvider.getBalance(address);
        
        console.log("Gas estimation:", {
          estimatedGas: estimatedGas.toString(),
          gasPrice: ethers.formatUnits(gasPrice, "gwei") + " gwei",
          estimatedCost: ethers.formatEther(estimatedCost) + " " + nativeCurrencySymbol,
          userBalance: ethers.formatEther(userBalance) + " " + nativeCurrencySymbol,
        });
        
        if (userBalance < balanceBuffer) {
          const shortfall = balanceBuffer - userBalance;
          const nativeSymbol = nativeCurrencySymbol;
          throw new Error(
            `Insufficient ${nativeSymbol} balance for deployment. ` +
            `You have ${parseFloat(ethers.formatEther(userBalance)).toFixed(4)} ${nativeSymbol} but need approximately ` +
            `${parseFloat(ethers.formatEther(balanceBuffer)).toFixed(4)} ${nativeSymbol}. ` +
            `Please add at least ${parseFloat(ethers.formatEther(shortfall)).toFixed(4)} ${nativeSymbol} to your wallet.`
          );
        }
      } catch (estimateErr) {
        // Re-throw if it's our custom error, otherwise log and continue
        if (estimateErr.message?.includes("Insufficient")) {
          throw estimateErr;
        }
        // MetaMask "Unauthorized" means the wallet account is not connected / session expired
        if (
          estimateErr.message?.includes("Unauthorized") ||
          estimateErr.message?.includes("unauthorized") ||
          estimateErr.code === 4100 ||
          estimateErr.error?.code === 4100
        ) {
          throw new Error(
            "Wallet authorization failed. Please disconnect and reconnect your wallet in MetaMask, then try again."
          );
        }
        console.warn("Gas estimation failed, proceeding anyway:", estimateErr);
      }

      // Fetch nonce via publicClient transport (not MetaMask) to avoid MetaMask RPC call
      try {
        txNonce = await publicClient.getTransactionCount({ address });
      } catch (nonceErr) {
        console.warn("Nonce fetch failed, letting MetaMask handle it:", nonceErr);
      }

      setPublishingStep("Deploying NFT collection on-chain...");
      // Ensure MetaMask's broadcast RPC is a working endpoint before the user signs.
      // wallet_addEthereumChain on an already-known chain shows an "Update network?" dialog
      // in MetaMask so the user can approve our Alchemy/drpc.org URL. After one approval,
      // MetaMask uses that RPC for all future broadcasts and the dialog no longer appears.
      await fixMetaMaskRpc(walletClient.chain?.id);

      // Pre-populated gas/nonce/fees from publicClient mean viem's prepareTransactionRequest
      // skips all read calls and MetaMask only receives eth_sendTransaction for signing.
      const feeRecipientArg = formik.values.royaltyRecipient &&
        formik.values.royaltyRecipient.toLowerCase() !== address?.toLowerCase()
          ? formik.values.royaltyRecipient
          : ethers.ZeroAddress;
      const createNFTHash = await walletClient.writeContract({
        address: networkContracts.communityFactory,
        abi: CommunityFactoryV2ABI,
        functionName: "createCommunity",
        args: [
          communityName,
          symbol,
          BigInt(formik.values.totalSupply || 1000),
          priceInSmallestUnit,
          paymentToken,
          BigInt(Math.min(royaltyFee, 1000)),
          `ipfs://${metadataCid}`,
          preMintMode,
          feeRecipientArg,
        ],
        account: address,
        chain: walletClient.chain,
        ...(txGas !== undefined && { gas: txGas }),
        ...(txNonce !== undefined && { nonce: txNonce }),
        ...(txMaxFeePerGas !== undefined && { maxFeePerGas: txMaxFeePerGas }),
        ...(txMaxPriorityFeePerGas !== undefined && { maxPriorityFeePerGas: txMaxPriorityFeePerGas }),
      });

      setPublishingStep(`Transaction sent! Waiting for confirmation... (${elapsedTime}s)`);

      // Update status to 'confirming'
      await updateDeploymentStatus({
        id: currentId,
        body: {
          txHash: createNFTHash,
          status: "confirming",
        },
      }).unwrap();

      // Step 7: Monitor transaction using our hook
      // On success, the transaction monitor will parse the contract address from events
      monitorTransaction(createNFTHash, {
        communityId: currentId,
        metadataCid,
        action: "create_collection",
      });

      // The rest will be handled by the transaction monitor's onSuccess callback
      
    } catch (err) {
      console.error("Publish error:", err);
      
      // Parse and format error message for better UX
      let errorMessage = err?.data?.message || err?.message || "Failed to publish community";
      
      // Handle common blockchain errors with user-friendly messages
      if (err?.code === "INSUFFICIENT_FUNDS" || errorMessage.toLowerCase().includes("insufficient funds")) {
        const bc = formik.values.blockchain;
        const nativeSymbol = bc === "matic" ? "POL" : bc ? bc.toUpperCase() : "ETH";
        errorMessage = `Insufficient ${nativeSymbol} balance for deployment. Please add more ${nativeSymbol} to your wallet and try again. Community NFT deployment requires approximately 0.01-0.02 ${nativeSymbol} for gas fees.`;
      } else if (err?.cause?.code === -32603 || err?.cause?.code === -32080 || errorMessage.includes("RPC endpoint returned") || errorMessage.includes("HTTP client error") || errorMessage.includes("httpStatus: 405") || err?.error?.code === -32002 || errorMessage.includes("too many errors") || errorMessage.includes("retrying in")) {
        // MetaMask's own configured Polygon RPC is broken. We tried to fix it via
        // wallet_addEthereumChain before the transaction, but the user may have rejected
        // the update dialog. Instruct them to fix it manually.
        errorMessage = "MetaMask's Polygon RPC is unreachable (HTTP error when broadcasting). " +
          "Please update it: MetaMask → ⋮ → Settings → Networks → Polygon Mainnet → " +
          "change RPC URL to https://polygon.drpc.org — then try again.";
      } else if (errorMessage.includes("authorization failed") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = "Wallet authorization failed. Please disconnect and reconnect your wallet in MetaMask, then try again.";
      } else if (err?.code === "ACTION_REJECTED" || errorMessage.includes("user rejected")) {
        errorMessage = "Transaction was rejected. Please try again and confirm the transaction in your wallet.";
      } else if (errorMessage.includes("network") || errorMessage.includes("chain")) {
        errorMessage = "Network error. Please check your wallet is connected to the correct network and try again.";
      }
      
      notify(errorMessage, "error");
      
      // Only mark deployment as failed if we actually started the on-chain deployment.
      // Without this guard, a pre-deployment error (e.g. editCommunity rejection) would
      // call updateDeploymentStatus({status:"failed"}) which resets state to pending_mint.
      if (communityId && deploymentStarted) {
        notify("Your community data has been saved. You can try publishing again.", "info");
        try {
          await updateDeploymentStatus({
            id: communityId,
            body: {
              status: "failed",
              errorMessage: errorMessage,
            },
          }).unwrap();
        } catch (updateErr) {
          console.error("Failed to update deployment status:", updateErr);
        }
      } else if (communityId) {
        notify("Please check your details and try again.", "info");
      }
      
      setPublishing(false);
      setPublishingStep("");
    }
  };

  // Whether this community is already published on-chain (locks certain fields)
  // Backend state is "active" (not "published") for live communities
  const isPublished = ["active", "sold_out"].includes(existingCommunity?.state);

  // Save edits for an already-published community (no blockchain interaction)
  const handleSaveEdits = async () => {
    try {
      await editCommunity({ id: communityId, body: formik.values }).unwrap();
      notify("Community updated successfully! 🎉", "success");
      navigate(`/communities/${communityId}`);
    } catch (err) {
      notify(err?.data?.message || "Failed to save changes", "error");
    }
  };

  // Auto-save draft when moving between steps (after step 1 is completed with basic info)
  const autoSaveDraft = useCallback(async () => {
    // Only auto-save if we have the minimum required fields
    const { name, shortDescription, artwork, category, blockchain, totalSupply } = formik.values;
    if (!name || !shortDescription || !artwork || !category || !blockchain) {
      // Still save to localStorage as backup even if incomplete
      saveDraftToStorage(formik.values);
      return; // Don't save to server without basic info
    }

    // Always save to localStorage as backup
    saveDraftToStorage(formik.values);

    try {
      if (communityId) {
        await editCommunity({ id: communityId, body: formik.values }).unwrap();
      } else {
        const saveResult = await saveCommunity({ body: formik.values }).unwrap();
        setCommunityId(saveResult.community._id);
        // Clear localStorage backup after successful server save
        clearDraftFromStorage();
      }
    } catch (err) {
      // Silent fail for auto-save - don't interrupt user flow
      console.log("Auto-save failed:", err);
    }
  }, [formik.values, communityId, saveCommunity, editCommunity, saveDraftToStorage, clearDraftFromStorage]);

  // Step navigation with auto-save
  const handleNextStep = async () => {
    if (activeStep < steps.length - 1) {
      // Auto-save when moving past step 0 (basic info)
      if (activeStep >= 0) {
        await autoSaveDraft();
      }
      setActiveStep(activeStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Step1BasicInfo
            formik={formik}
            handleImageUpload={handleImageUpload}
            uploadingImage={uploadingImage}
            isPublished={isPublished}
          />
        );
      case 1:
        return <Step2CommunityLinks formik={formik} />;
      case 2:
        return <Step3SaleSettings formik={formik} isPublished={isPublished} />;
      case 3:
        return <Step4PerksAccess formik={formik} />;
      case 4:
        return (
          <Step5AdvancedSettings formik={formik} userWalletAddress={address} isPublished={isPublished} />
        );
      case 5:
        return (
          <Step6Preview
            formik={formik}
            onPublish={handlePublish}
            publishing={publishing}
            publishingStep={publishingStep}
            isPublished={isPublished}
            onSaveEdits={handleSaveEdits}
            savingEdits={editing}
          />
        );
      default:
        return null;
    }
  };

  if (loadingCommunity) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Bars height="50" width="50" color="#8538ff" visible={true} />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 roboto-font">
      <MetaTags
        title={editId ? "Edit Community" : "Create Community"}
        description="Create a loyalty membership NFT community on FunDeSci"
        link="/create-community"
      />

      <Container className="pt-28 pb-20">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 abel-font bg-gradient-to-r from-[#8538ff] to-[#7e7cff] bg-clip-text text-transparent">
            {editId ? "Edit Community" : "Create Loyalty Community"}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Build your DeSci community, mint loyalty NFTs, and offer exclusive perks to your members
          </p>
        </div>

        {/* Steps Indicator */}
        <div className="mb-8 md:mb-10">
          {/* Mobile View - Compact */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-4 px-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                    {activeStep + 1}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800">{steps[activeStep].label}</div>
                    <div className="text-xs text-gray-500">{steps[activeStep].description}</div>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-purple-500 transition-all duration-300"
                    style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1">Step {activeStep + 1} of {steps.length}</div>
              </div>
            </div>
          </div>

          {/* Desktop View - Full */}
          <div className="hidden md:flex justify-center">
            <div className="flex items-center gap-3 lg:gap-4">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <button
                    type="button"
                    onClick={() => setActiveStep(index)}
                    className="flex flex-col items-center min-w-[90px] lg:min-w-[110px] group transition-all"
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg transition-all shadow-sm ${
                        index === activeStep
                          ? "bg-gradient-to-br from-primary to-purple-600 text-white scale-110 shadow-lg"
                          : index < activeStep
                          ? "bg-gradient-to-br from-green-500 to-green-600 text-white"
                          : "bg-white border-2 border-gray-300 text-gray-400 group-hover:border-primary group-hover:text-primary"
                      }`}
                    >
                      {index < activeStep ? (
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="text-center mt-2">
                      <div
                        className={`text-sm font-medium transition-colors ${
                          index === activeStep ? "text-primary" : "text-gray-600"
                        }`}
                      >
                        {step.label}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {step.description}
                      </div>
                    </div>
                  </button>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-12 lg:w-16 h-1 rounded-full transition-all ${
                        index < activeStep 
                          ? "bg-gradient-to-r from-green-500 to-green-600" 
                          : "bg-gray-200"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 sm:p-6 md:p-10 border border-gray-100">
          <form onSubmit={formik.handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            {activeStep < steps.length - 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 mt-8 md:mt-10 pt-6 border-t border-gray-100">
                <div className="order-2 sm:order-1">
                  {activeStep > 0 && (
                    <ChangeStepButton type="prev" onClick={handlePrevStep} />
                  )}
                </div>
                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 order-1 sm:order-2">
                  <button
                    type="submit"
                    disabled={saving || editing}
                    className="px-5 py-3 sm:py-2.5 text-sm sm:text-base text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-all disabled:opacity-50 border border-gray-200"
                  >
                    {saving || editing ? "Saving..." : "💾 Save Draft"}
                  </button>
                  <ChangeStepButton
                    type="next"
                    onClick={handleNextStep}
                    label={activeStep === steps.length - 2 ? "Preview" : "Next Step"}
                  />
                </div>
              </div>
            )}

            {/* Back button on preview */}
            {activeStep === steps.length - 1 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <ChangeStepButton type="prev" onClick={handlePrevStep} />
              </div>
            )}
          </form>
        </div>
      </Container>
    </div>
  );
};

export default CreateCommunity;
